/**
 * A character written whole, flashing green over a drill square and flying
 * into the wedge of the pie it fills.
 *
 * A copy of the character's outline is laid over the square, filled green,
 * and the stylesheet fades it. It is a layer of its own rather than the
 * drawing coloured, so the drill can move on at once: to the next pass, where
 * the drawing is emptied, or in an Exercise to the next character, where it is
 * replaced. A second copy shrinks across the page into the pie, so that a
 * reader sees what a wedge is for. `xiezi-square.ts` calls both and
 * `modules/cidian-xiezi` colours them.
 */

/** How long the flash lasts. `modules/cidian-xiezi` animates over the same. */
export const flashLength = 900;

/** How long the copy takes to reach the pie. */
export const flightLength = 650;

/**
 * The character's outline alone, green, ready to lay over something.
 *
 * The brushes, the clip paths their ids name and the demonstration brushes
 * are taken out, so nothing in it collides with the drawing it was copied
 * from or reads as one of the reader's strokes.
 */
function greenOutline(drawing: Element): SVGElement | undefined {
  const copy = drawing.cloneNode(true);

  if (!(copy instanceof SVGElement)) {
    return undefined;
  }

  for (const part of copy.querySelectorAll(
    "defs, .cidian-bishun-ink, .cidian-xiezi-demos",
  )) {
    part.remove();
  }

  for (const cue of copy.querySelectorAll("[data-cb-xiezi-cue]")) {
    cue.removeAttribute("data-cb-xiezi-cue");
  }

  copy
    .querySelector(".cidian-bishun-guide")
    ?.setAttribute("class", "cidian-xiezi-flash-strokes");

  return copy;
}

/**
 * Lay the character's outline over the square, green, and take it away again
 * once it has faded.
 *
 * @param drawing - The character's drawing, in the square.
 * @returns The overlay, or nothing where the drawing would not copy.
 */
export function flashCharacter(drawing: Element): Element | undefined {
  const overlay = greenOutline(drawing);

  if (overlay === undefined) {
    return undefined;
  }

  overlay.classList.add("cidian-xiezi-flash");
  drawing.after(overlay);
  setTimeout(() => {
    overlay.remove();
  }, flashLength);

  return overlay;
}

/**
 * Send a green copy of the character from the square into a wedge of a pie.
 *
 * It is fixed to the viewport over the square and moved by the Web Animations
 * API, shrinking onto the middle of the target. It is placed through the
 * CSSOM, since the site's content security policy refuses a `style`
 * attribute. A browser without the API, and
 * a reader who has asked for less motion, see the wedge fill without it.
 *
 * @param drawing - The character's drawing, in the square.
 * @param target - What it flies into. Nothing, where there are no pies.
 */
export function flyCharacter(
  drawing: Element,
  target: Element | undefined,
): void {
  const copy = greenOutline(drawing);
  const view = drawing.ownerDocument.defaultView;

  if (
    target === undefined ||
    copy === undefined ||
    typeof copy.animate !== "function" ||
    view?.matchMedia("(prefers-reduced-motion: reduce)").matches !== false
  ) {
    return;
  }

  const from = drawing.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  const scale = Math.max(to.width, to.height) / Math.max(from.width, 1);
  const x = to.left + to.width / 2 - (from.left + from.width / 2);
  const y = to.top + to.height / 2 - (from.top + from.height / 2);

  copy.classList.add("cidian-xiezi-flight");
  copy.style.left = `${String(from.left)}px`;
  copy.style.top = `${String(from.top)}px`;
  copy.style.width = `${String(from.width)}px`;
  copy.style.height = `${String(from.height)}px`;
  drawing.ownerDocument.body.append(copy);
  copy
    .animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: 0.9 },
        {
          transform: `translate(${String(x)}px, ${String(y)}px) scale(${String(scale)})`,
          opacity: 0.6,
        },
      ],
      { duration: flightLength, easing: "cubic-bezier(0.5, 0, 0.75, 0)" },
    )
    .finished.finally(() => {
      copy.remove();
    })
    .catch(() => undefined);
}
